import { useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Input, Typography } from "@altimateai/lego";
import * as Yup from "yup";
import { useFormik } from "formik";
import { isFreeStack } from "../../helpers/tenant";
import { Navigate, useNavigate } from "react-router-dom";
import { FormAlert, FormSubmit } from "./components/form";
import { BaseContainer } from "./components/base";
import { isFormFieldError } from "./helper";
import { useMutation, useQuery } from "@tanstack/react-query";
import { loginUser } from "../../services/auth";
import styles from "./components/styles.module.scss";
import Session from "supertokens-auth-react/recipe/session";
import { getSSOUserInfo, postRegisterSSO } from "@/helpers/apis";
import { useAppDispatch, useAppState } from "@/modules/app/useAppContext";
import { setLoggedinUser } from "@/modules/app/appSlice";
import { toast } from "@altimateai/lego";

const GET_SSO_USER_INFO = "GET_SSO_USER_INFO";

const SSORegister = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { ssoGroupSyncEnabled } = useAppState();
  // Ref to read the latest flag value inside the delayed setTimeout callback,
  // since tenant info may load after the effect runs but before the 1500ms fires.
  const ssoGroupSyncEnabledRef = useRef(ssoGroupSyncEnabled);
  ssoGroupSyncEnabledRef.current = ssoGroupSyncEnabled;

  const { data: ssoUserInfo, isLoading } = useQuery({
    queryKey: [GET_SSO_USER_INFO],
    queryFn: getSSOUserInfo,
    gcTime: 300000,
  });

  // Handle successful SSO user info fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (
      !ssoUserInfo ||
      !("auth_token" in ssoUserInfo) ||
      !ssoUserInfo.auth_token ||
      !ssoUserInfo.first_name
    )
      return;

    loginUser(ssoUserInfo);
    dispatch(setLoggedinUser(ssoUserInfo));

    // Notify Owner/Admin about pending role conflicts after SSO login.
    // This timer is intentionally NOT cleaned up on unmount: navigate() below
    // unmounts this component, but the toast must still fire after the delay.
    // toast() is a global (Sonner) call — it works regardless of component lifecycle.
    if (ssoUserInfo.has_role_conflict && ssoUserInfo.role_FK) {
      const role = ssoUserInfo.role_FK.role_title;
      if (role === "Owner" || role === "Admin") {
        setTimeout(() => {
          if (!ssoGroupSyncEnabledRef.current) return;
          toast({
            title: "Role conflict detected",
            description: `SSO suggests "${ssoUserInfo.sso_resolved_role_title ?? "a different role"}". Review in Settings → Users.`,
            duration: 8000,
          });
        }, 1500);
      }
    }

    navigate(isFreeStack() ? "/quickstart" : "/summary");
  }, [ssoUserInfo, dispatch, navigate]);

  useEffect(() => {
    const checkSession = async () => {
      const sessionExists = await Session.doesSessionExist();
      if (!sessionExists) {
        return <Navigate to="/login" state={{ from: window.location.pathname, expired: true }} />;
      }
    };
    checkSession();
  }, []);

  const { mutate, error } = useMutation({
    mutationFn: postRegisterSSO,
    onSuccess: response => {
      if (response?.auth_token && response?.first_name) {
        loginUser(response);
        navigate(isFreeStack() ? "/quickstart" : "/summary");
      }
    },
  });
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: "",
      lastName: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please Enter Your First Name"),
      lastName: Yup.string().required("Please Enter Your Last Name"),
    }),
    onSubmit: values => mutate({ ...values }),
  });

  if (isLoading) {
    return null;
  }

  const showFormFieldError = isFormFieldError(validation);
  const _error =
    error?.message || showFormFieldError("lastName") || showFormFieldError("firstName");

  const isFormDisabled = () => {
    return false;
  };
  return (
    <BaseContainer footer={<></>}>
      <Typography variant="page-heading">Complete your SSO account setup</Typography>
      <Typography className="al-text-muted-foreground al-mt-2">
        Please provide the following details to complete your account setup.
      </Typography>

      <form
        onSubmit={e => {
          e.preventDefault();
          validation.handleSubmit();
        }}
        className="mt-3"
      >
        <div className="mb-3">
          <Label htmlFor="firstName" className="form-label">
            First Name <span className={styles.asterisk}>*</span>
          </Label>
          <Input
            name="firstName"
            type="text"
            placeholder="Enter First Name"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.firstName}
          />
        </div>

        <div className="mb-3">
          <Label htmlFor="lastName" className="form-label">
            Last Name <span className={styles.asterisk}>*</span>
          </Label>
          <Input
            name="lastName"
            type="text"
            placeholder="Enter Last Name"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.lastName}
          />
        </div>

        <FormAlert error={_error} />

        <div className="mt-4">
          <FormSubmit disabled={isFormDisabled()}>Sign Up</FormSubmit>
        </div>
      </form>
    </BaseContainer>
  );
};

export { SSORegister };
